import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Switch from "@mui/material/Switch";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import {
  getNotificationSetting,
  updateNotificationSetting,
} from "../../services/home.services";
import { useAlert } from "../../contexts/Alert";
import { Divider } from "@mui/material";
import { trackClickEvent } from "../../mixpanel";

const notificationCategories = [
  {
    label: "Posts",
    type: "posts",
    description: "",
    showSourceSelector: true,
    sourceLabel: "Allow Post Notifications From",
    sourceTooltip:
      "Choose which providers you want to receive post notifications from",
    subCategories: [
      {
        label: "New Posts",
        type: "new_posts",
        showNotificationTypes: true,
        tooltip: "Get notified when a provider creates a new post",
      },
      {
        label: "Replies",
        type: "replies",
        showNotificationTypes: true,
        tooltip: "Get notified when someone replies to your post or comment",
      },
      {
        label: "Likes",
        type: "likes",
        showNotificationTypes: true,
        tooltip: "Get notified when someone likes your post or comment",
      },
    ],
  },
  {
    label: "Referrals",
    type: "referrals",
    description:
      "You'll receive referral notifications from all providers by default, so you don't miss important opportunities. Manage how you're notified below.",
    subCategories: [
      {
        label: "Sent",
        type: "sent",
        showNotificationTypes: true,
        tooltip: "Get notified when a provider responds to your referral",
      },
      {
        label: "Received",
        type: "received",
        showNotificationTypes: true,
        tooltip: "Get notified when a provider sends you a referral",
      },
      {
        label: "Contact Information Shared",
        type: "share_profile",
        showNotificationTypes: true,
        tooltip: "Get notified when your contact info is shared with someone",
      },
    ],
  },
  {
    label: "Availability Updates",
    type: "availability",
    description: "",
    showSourceSelector: true,
    sourceLabel: "Allow Updates From",
    sourceTooltip:
      "Choose which providers you want to receive and send availability updates to",
    subCategories: [
      {
        label: "Updates from Others",
        type: "updates",
        showNotificationTypes: true,
        tooltip: "Get notified when providers update their availability",
      },
      {
        label: "My Updates",
        type: "notify",
        showNotificationTypes: true,
        notificationTypes: [
          {
            label: "Notify others when I update my availability",
            type: "others",
          },
        ],
        tooltip: "Let providers know when you update your availability",
      },
      // {
      //   label: "Messages",
      //   type: "notify_message",
      //   showNotificationTypes: true,
      //   notificationTypes: [
      //     {
      //       label: "Email me when I receive a new direct message or a group update",
      //       type: "others",
      //     },
      //   ],
      //   tooltip: "Notify yourself when new message or group update received",
      // },
    ],
  },
  {
    label: "Messages",
    type: "chat",
    description: "",
    subCategories: [
      {
        label: "Messages",
        type: "notify",
        showNotificationTypes: true,
        notificationTypes: [
          {
            label:
              "Notify me about new messages and group updates",
            type: "messages",
          },
        ],
        tooltip: "Includes direct messages and updates from groups you’re part of.",
      },
    ],
  },
];

const notificationTypes = [
  { label: "In-app", type: "in_app" },
  { label: "Email", type: "email" },
];

const notificationSourceOptions = [
  {
    label: "All Providers",
    value: "all_providers",
    tooltip: "Providers across the entire TTN community",
  },
  {
    label: "My Trusted Network Only",
    value: "first_connection",
    tooltip: "Only providers you've added to your trusted network",
  },
  {
    label: "My Trusted Network + Who They Trust",
    value: "second_connection",
    tooltip: "Your trusted providers and the providers they trust",
  },
];

const frequencyOptions = [
  {
    label: "Immediate",
    value: "immediate",
    tooltip: "Get individual notifications right away",
  },
  {
    label: "Daily Summary",
    value: "daily",
    tooltip: "Bundle all post notifications into one daily email",
  },
  {
    label: "Weekly Summary",
    value: "weekly",
    tooltip: "Bundle all post notifications into one weekly email",
  },
];

/**
 * Generate default notification settings object
 * @returns {Object}
 */
const getDefaultSettings = () => {
  const defaultSettings = {};
  notificationCategories.forEach((category) => {
    category.subCategories.forEach((subCategory) => {
      if (subCategory.showNotificationTypes) {
        if (subCategory.notificationTypes) {
          subCategory.notificationTypes.forEach((notifType) => {
            defaultSettings[
              `${category.type}_${subCategory.type}_${notifType.type}`
            ] = 1;
          });
        } else {
          notificationTypes.forEach((notifType) => {
            defaultSettings[
              `${category.type}_${subCategory.type}_${notifType.type}`
            ] = 1;
          });
        }
      } else {
        defaultSettings[`${category.type}_${subCategory.type}`] = 1;
      }
    });

    // Add default frequency for each category
    defaultSettings[`${category.type}_frequency`] = "immediate";
  });
  defaultSettings.allow_post_notifications_from = "all_providers";
  defaultSettings.allow_availability_notifications_from = "first_connection";
  return defaultSettings;
};

const CategorySection = React.memo(function CategorySection({
  category,
  notificationSettings,
  disableSwitch,
  handleSourceChange,
  handleToggleChange,
  handleFrequencyChange,
}) {
  const areAllTogglesOff = (subCategory) => {
    if (!subCategory.showNotificationTypes) {
      const settingKey = `${category.type}_${subCategory.type}`;
      return !notificationSettings[settingKey];
    }
    if (subCategory.notificationTypes) {
      return subCategory.notificationTypes.every((notifType) => {
        const settingKey = `${category.type}_${subCategory.type}_${notifType.type}`;
        return !notificationSettings[settingKey];
      });
    }
    return notificationTypes.every((notifType) => {
      const settingKey = `${category.type}_${subCategory.type}_${notifType.type}`;
      return !notificationSettings[settingKey];
    });
  };

  const getSourceValue = () => {
    if (category.type === "posts") {
      return (
        notificationSettings.allow_post_notifications_from || "all_providers"
      );
    } else if (category.type === "availability") {
      return (
        notificationSettings.allow_availability_notifications_from ||
        "first_connection"
      );
    }
    return "all_providers";
  };

  const handleSingleToggle = (subCategory) => {
    const settingKey = `${category.type}_${subCategory.type}`;
    const newValue = notificationSettings[settingKey] === 0 ? 1 : 0;
    handleToggleChange(category.type, subCategory.type, null, newValue);
  };

  const getFrequencyValue = () => {
    return notificationSettings[`${category.type}_frequency`] || "immediate";
  };

  return (
    <div>
      <Box sx={{ mt: 3, ml: 1, display: "flex", flexDirection: "column" }}>
        <Typography
          variant="body"
          sx={{ fontSize: 24, fontWeight: 500, mb: 1 }}
        >
          {category.label}
        </Typography>
        {category.description && (
          <Typography variant="body" sx={{ fontSize: 14, color: "#61676B" }}>
            {category.description}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          p: 2,
          m: 1,
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        {category.showSourceSelector && (
          <>
            <Box
              sx={{
                display: "inline-flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 500, fontSize: 15 }}
                >
                  {category.sourceLabel}
                </Typography>
                <Tooltip
                  title={category.sourceTooltip}
                  placement="right"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        fontSize: ".8rem",
                        marginBottom: "12px",
                      },
                    },
                  }}
                >
                  <InfoIcon sx={{ color: "#0C8CE9", fontSize: 16 }} />
                </Tooltip>
              </Box>
            </Box>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <Select
                value={getSourceValue()}
                onChange={(e) => handleSourceChange(category.type, e)}
                disabled={disableSwitch}
              >
                {notificationSourceOptions.map((source) => (
                  <MenuItem key={source.value} value={source.value}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography>{source.label}</Typography>
                      <Tooltip
                        title={source.tooltip}
                        placement="right"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              fontSize: ".8rem",
                              marginBottom: "12px",
                            },
                          },
                        }}
                      >
                        <InfoIcon sx={{ color: "#0C8CE9", fontSize: 16 }} />
                      </Tooltip>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Divider sx={{ borderWidth: 1, mb: 3 }} />
          </>
        )}
        {category.type === "posts" && (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                mb: 2,
              }}
            >
              <Typography
                variant="body1"
                sx={{ fontWeight: 500, fontSize: 15 }}
              >
                Post Notification Frequency
              </Typography>
            </Box>
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <RadioGroup
                value={getFrequencyValue()}
                onChange={(e) => handleFrequencyChange(category.type, e)}
              >
                {frequencyOptions.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio disabled={disableSwitch} />}
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography variant="body1">{option.label}</Typography>
                        <Tooltip
                          title={option.tooltip}
                          placement="right"
                          componentsProps={{
                            tooltip: {
                              sx: {
                                fontSize: ".8rem",
                                marginBottom: "12px",
                              },
                            },
                          }}
                        >
                          <InfoIcon sx={{ color: "#0C8CE9", fontSize: 16 }} />
                        </Tooltip>
                      </Box>
                    }
                    sx={{ mb: 1 }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
            <Divider sx={{ borderWidth: 1, mb: 3 }} />
          </>
        )}
        {category.subCategories.map((subCategory, subCategoryIndex) => (
          <Box key={subCategoryIndex}>
            <Box
              sx={{
                display: "flex",
                maxWidth: "363px",
                alignItems: "center",
                justifyContent: "space-between",
                mt: 2,
                gap: 3,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {subCategory.label}
                </Typography>
                <Tooltip
                  title={subCategory.tooltip}
                  placement="right"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        fontSize: ".8rem",
                        marginBottom: "12px",
                      },
                    },
                  }}
                >
                  <InfoIcon sx={{ color: "#0C8CE9", fontSize: 16 }} />
                </Tooltip>
              </Box>
              {!subCategory.showNotificationTypes && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 3,
                    alignItems: "center",
                    maxWidth: "215px",
                  }}
                >
                  <Switch
                    disabled={disableSwitch}
                    checked={
                      !!notificationSettings[
                        `${category.type}_${subCategory.type}`
                      ]
                    }
                    onChange={() => handleSingleToggle(subCategory)}
                    inputProps={{ "aria-label": "controlled" }}
                    sx={{
                      border: "2px solid yellow",
                    }}
                  />
                </Box>
              )}
            </Box>
            {subCategory.showNotificationTypes && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box sx={{ display: "flex", gap: 15 }}>
                  {(subCategory.notificationTypes || notificationTypes).map(
                    (notifType, notifIndex) => {
                      const settingKey = `${category.type}_${subCategory.type}_${notifType.type}`;
                      return (
                        <Box
                          key={notifIndex}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 3,
                          }}
                        >
                          <Typography variant="body1">
                            {notifType.label}
                          </Typography>
                          <Switch
                            disabled={disableSwitch}
                            checked={!!notificationSettings[settingKey]}
                            onChange={() =>
                              handleToggleChange(
                                category.type,
                                subCategory.type,
                                notifType.type,
                              )
                            }
                            inputProps={{ "aria-label": "controlled" }}
                          />
                        </Box>
                      );
                    },
                  )}
                </Box>
                {areAllTogglesOff(subCategory) && (
                  <Typography
                    variant="body2"
                    color="#A3A6A8"
                    sx={{ mt: 1, fontSize: 14 }}
                  >
                    You've turned off all notifications. You won't receive
                    alerts unless one option is enabled.
                  </Typography>
                )}
              </Box>
            )}
            {category.subCategories.length - 1 !== subCategoryIndex && (
              <Divider sx={{ borderWidth: 1, my: 1 }} />
            )}
          </Box>
        ))}
      </Box>
    </div>
  );
});

CategorySection.propTypes = {
  category: PropTypes.object.isRequired,
  notificationSettings: PropTypes.object.isRequired,
  disableSwitch: PropTypes.bool.isRequired,
  handleSourceChange: PropTypes.func.isRequired,
  handleToggleChange: PropTypes.func.isRequired,
  handleFrequencyChange: PropTypes.func.isRequired,
};

const NotificationSettings = () => {
  const [notificationSettings, setNotificationSettings] = useState(null);
  const [disableSwitch, setDisableSwitch] = useState(false);
  const { sendAlert } = useAlert();

  const handleToggleChange = useCallback(
    (category, subCategory, notificationType, newValue) => {
      const settingKey = notificationType
        ? `${category}_${subCategory}_${notificationType}`
        : `${category}_${subCategory}`;
      const finalValue =
        newValue !== undefined
          ? newValue
          : notificationSettings[settingKey] === 0
            ? 1
            : 0;

      setDisableSwitch(true);
      updateNotificationSetting({
        ...notificationSettings,
        [settingKey]: finalValue,
      })
        .then((res) => {
          sendAlert(res.data.message, "success");
          trackClickEvent(
            `Updated Notification Settings - ${category}_${subCategory}`,
            {
              page: "Notification Settings",
              component: category,
              toggle: finalValue ? 1 : 0,
            },
          );
          setNotificationSettings((prev) => ({
            ...prev,
            [settingKey]: finalValue,
          }));
          setDisableSwitch(false);
        })
        .catch((error) => {
          setDisableSwitch(false);
          sendAlert(error.response?.data?.message, "error");
        });
    },
    [notificationSettings, sendAlert],
  );

  const handleSourceChange = useCallback(
    (categoryType, event) => {
      const newSource = event.target.value;
      const settingKey =
        categoryType === "posts"
          ? "allow_post_notifications_from"
          : "allow_availability_notifications_from";

      setDisableSwitch(true);
      updateNotificationSetting({
        ...notificationSettings,
        [settingKey]: newSource,
      })
        .then((res) => {
          sendAlert(res.data.message, "success");
          setNotificationSettings((prev) => ({
            ...prev,
            [settingKey]: newSource,
          }));
          trackClickEvent(
            `Updated Notification Settings - ${categoryType}_${newSource}`,
            {
              page: "Notification Settings",
              component: categoryType,
              value: newSource,
            },
          );
          setDisableSwitch(false);
        })
        .catch((error) => {
          setDisableSwitch(false);
          sendAlert(error.response?.data?.message, "error");
        });
    },
    [notificationSettings, sendAlert],
  );

  const handleFrequencyChange = useCallback(
    (categoryType, event) => {
      if (categoryType !== "posts") return;
      const newFrequency = event.target.value;
      const settingKey = `${categoryType}_frequency`;
      setDisableSwitch(true);
      updateNotificationSetting({
        ...notificationSettings,
        [settingKey]: newFrequency,
      })
        .then((res) => {
          sendAlert(res.data.message, "success");
          setNotificationSettings((prev) => ({
            ...prev,
            [settingKey]: newFrequency,
          }));
          trackClickEvent(
            `Updated Notification Settings - ${categoryType}_frequency`,
            {
              page: "Notification Settings",
              component: categoryType,
              toggle: newFrequency,
            },
          );
          setDisableSwitch(false);
        })
        .catch((error) => {
          setDisableSwitch(false);
          sendAlert(error.response?.data?.message, "error");
        });
    },
    [notificationSettings, sendAlert],
  );

  useEffect(() => {
    getNotificationSetting()
      .then((res) => {
        if (!res.data.data) {
          setNotificationSettings(getDefaultSettings());
        } else {
          const settings = {
            ...getDefaultSettings(),
            ...res.data.data,
            allow_post_notifications_from:
              res.data.data.allow_post_notifications_from || "all_providers",
            allow_availability_notifications_from:
              res.data.data.allow_availability_notifications_from ||
              "first_connection",
            posts_frequency: res.data.data.posts_frequency || "immediate",
          };
          setNotificationSettings(settings);
        }
      })
      .catch((error) => {
        sendAlert(error.response?.data?.message, "error");
      });
  }, [sendAlert]);

  if (!notificationSettings) return null;

  return (
    <Paper sx={{ display: "flex", flexDirection: "column", p: 3, pt: 1 }}>
      {notificationCategories.map((category, idx) => (
        <CategorySection
          key={category.type}
          category={category}
          notificationSettings={notificationSettings}
          disableSwitch={disableSwitch}
          handleSourceChange={handleSourceChange}
          handleToggleChange={handleToggleChange}
          handleFrequencyChange={handleFrequencyChange}
        />
      ))}
    </Paper>
  );
};

export default NotificationSettings;
